import { useLocation } from 'react-router';

import { useI18n } from '#/i18n/hooks/useI18n';
import { useEmbedSearchWidget } from '#/modules/embedSearchWidget/contexts/EmbedSearchWidgetContext';
import {
  useIsSearching,
  useSearchCurrentPageResults,
  useSearchFiltersByName,
  useSearchLocation,
  useSearchPageIndex,
  useSearchQuery,
  useSearchRadius,
  useSearchResultsByPage,
  useSearchSort,
} from '#/modules/search/contexts/SearchContext';
import type { SearchPageLocationState } from '#/modules/search/pages/Search/types/SearchPageLocationState';
import { searchGetPathSwitch } from '#/modules/search/routing/helpers/searchGetPathSwitch';
import { getSearchHitElementId } from '#/modules/search/utils/getSearchHitElementId';

import type { SearchContextHeaderLocationState } from './SearchContextHeaderLocationState';

type Args = {
  currentObjectId: string;
};

export function useSearchContextHeaderInput({ currentObjectId }: Args) {
  const { locale } = useI18n();
  const { embedSearchWidget } = useEmbedSearchWidget();
  const location = useLocation<SearchContextHeaderLocationState>();
  const showSearchContext = location.state?.showSearchContext;

  const filtersByName = useSearchFiltersByName();
  const pageIndex = useSearchPageIndex();
  const query = useSearchQuery();
  const results = useSearchCurrentPageResults();
  const resultsByPage = useSearchResultsByPage();
  const searchRadius = useSearchRadius();
  const sort = useSearchSort();
  const isSearching = useIsSearching();
  const searchLocation = useSearchLocation();

  if (!results || !showSearchContext) {
    return { searchContextInput: undefined, isSearching };
  }

  const currentResultIndex = results.hits.findIndex(
    (hit) => hit.objectID === currentObjectId,
  );

  if (currentResultIndex === -1) return {};

  let previousResult = null;
  let previousResultPageIndex = null;

  if (currentResultIndex > 0) {
    previousResult = resultsByPage[pageIndex]?.hits[currentResultIndex - 1];
    previousResultPageIndex = pageIndex;
  } else if (pageIndex > 0) {
    const previousPage = resultsByPage[pageIndex - 1];

    if (previousPage) {
      previousResult = previousPage.hits[(previousPage.hitsPerPage || 0) - 1];

      if (previousResult) {
        previousResultPageIndex = pageIndex - 1;
      }
    }
  }

  let nextResult = null;
  let nextResultPageIndex = null;

  if (currentResultIndex < (results.hitsPerPage || 0) - 1) {
    nextResult = results.hits[currentResultIndex + 1];
    nextResultPageIndex = results.page;
  } else if (pageIndex < results.nbPages - 1) {
    const nextPage = resultsByPage[pageIndex + 1];

    if (nextPage) {
      [nextResult] = nextPage.hits;
      nextResultPageIndex = pageIndex + 1;
    }
  }

  const searchResultsUrl =
    searchGetPathSwitch({
      locale,
      filtersByName,
      q: query,
      pageIndex,
      sort,
      radius: searchRadius,
      embedSearchWidgetId: embedSearchWidget?.id ?? null,
    }) + `#${getSearchHitElementId(currentObjectId)}`;

  const searchResultsState = {
    searchLocation,
  } satisfies SearchPageLocationState;

  return {
    searchContextInput: {
      currentResultIndex,
      pageIndex,
      resultsByPage,
      searchResultsUrl,
      searchResultsState,
      previousResult,
      previousResultPageIndex,
      nextResult,
      nextResultPageIndex,
    },
    isSearching,
  };
}

export type SearchContextInput = NonNullable<
  ReturnType<typeof useSearchContextHeaderInput>['searchContextInput']
>;
